/**
 * Shared signal for "is at least one instance of a given page shell
 * currently mounted."
 *
 * A plain window event broadcast is fire-and-forget: a listener that
 * subscribes AFTER the event already fired never learns the current state,
 * only future transitions. If AppLayout is already mounted from a prior
 * commit and a *sibling* component (e.g. ChangelogModal, mounted once at the
 * App.tsx route-wrapper level) mounts fresh later — for example after the
 * global error boundary resets while auth/organization state stays hydrated,
 * so AppLayout itself never unmounts/remounts — there is no new "mounted"
 * event for the fresh listener to catch, and it would be stuck reading
 * "not mounted" forever even though AppLayout is right there.
 *
 * Each signal keeps a module-level count (not just a boolean, so two
 * instances of the same shell briefly overlapping during a route transition
 * don't race each other to a false "unmounted") that any consumer can read
 * synchronously the moment it starts caring, in addition to subscribing to
 * future changes — mirrors usePersonalizationPreferences.tsx's own
 * module-level store, built for exactly the same class of problem.
 *
 * There are two independent signals, one per shell, rather than one shared
 * "some shell is mounted" signal: AppLayout is the only shell that mounts
 * the Solomon AI panel and its open-solomon listener (ChangelogModal's
 * "Share feedback" CTA depends on exactly that), so collapsing AdminLayout
 * into the same signal would make that CTA appear on /admin/* routes where
 * clicking it would silently do nothing. Callers that only care about "does
 * SOME supported shell exist right now" (e.g. ChangelogModal's auto-popup
 * gate, since ChangelogEntryPointButton itself works the same in either
 * shell) combine both signals themselves (Codex P2, PR #6347).
 */
function createMountSignal() {
  let mountCount = 0;
  const listeners = new Set<(mounted: boolean) => void>();

  function isMounted(): boolean {
    return mountCount > 0;
  }

  function report(mounted: boolean): void {
    mountCount += mounted ? 1 : -1;
    const current = mountCount > 0;
    listeners.forEach((listener) => listener(current));
  }

  function subscribe(listener: (mounted: boolean) => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function reset(): void {
    mountCount = 0;
    listeners.clear();
  }

  return { isMounted, report, subscribe, reset };
}

const appLayoutSignal = createMountSignal();
const adminLayoutSignal = createMountSignal();
const appLayoutChangelogEntryPointSignal = createMountSignal();
const chromeFreeKeyboardScopeSignal = createMountSignal();

/** A task owns global keyboard scope even when another shell briefly overlaps in a transition. */
export const isChromeFreeKeyboardScopeMounted = chromeFreeKeyboardScopeSignal.isMounted;
export const reportChromeFreeKeyboardScopeMounted = chromeFreeKeyboardScopeSignal.report;
export const subscribeChromeFreeKeyboardScopeMounted = chromeFreeKeyboardScopeSignal.subscribe;

export const isAppLayoutMounted = appLayoutSignal.isMounted;
/** Existing callers describe a standard shell; chrome-free reports presence separately. */
export function reportAppLayoutMounted(mounted: boolean, hasChangelogEntryPoint = true): void {
  appLayoutSignal.report(mounted);
  if (hasChangelogEntryPoint) appLayoutChangelogEntryPointSignal.report(mounted);
}

export const isAppLayoutChangelogEntryPointMounted = appLayoutChangelogEntryPointSignal.isMounted;
export const reportAppLayoutChangelogEntryPointMounted = appLayoutChangelogEntryPointSignal.report;
export const subscribeAppLayoutChangelogEntryPointMounted = appLayoutChangelogEntryPointSignal.subscribe;
export const subscribeAppLayoutMounted = appLayoutSignal.subscribe;

export const isAdminLayoutMounted = adminLayoutSignal.isMounted;
export const reportAdminLayoutMounted = adminLayoutSignal.report;
export const subscribeAdminLayoutMounted = adminLayoutSignal.subscribe;

/** Test-only: reset both shells' module state between test cases. */
export function __resetAppLayoutMountSignalForTests(): void {
  appLayoutSignal.reset();
  adminLayoutSignal.reset();
  appLayoutChangelogEntryPointSignal.reset();
  chromeFreeKeyboardScopeSignal.reset();
}
